import { Component, OnDestroy, OnInit } from '@angular/core';
import { AtlasBaseMapComponent } from '../atlas-base-map.component';
import { HttpClient } from '@angular/common/http';
import { BootstrapMediaService } from 'src/app/services/bootstrap-media.service';
import { TranslationHelperService } from 'src/app/services/translations/translation-helper.service';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { POINTS_LABEL_ATLAS_FIX } from 'src/assets/constants/constants';
import GeoJSON from 'ol/format/GeoJSON';
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { unsubscribe } from 'src/app/utils/helpers';
import { AtlasFixFilterService } from 'src/app/services/filter/atlas-fix-filter.service';
import { PointDetails } from 'src/app/models/pointDetails';
import { catchError, map, of, Subject, switchMap } from 'rxjs';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { GeoserverAPIService } from 'src/app/services/geoserver/geoserver-api.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-atlas-fix-map',
    templateUrl: './atlas-fix-map.component.html',
    styleUrls: ['./atlas-fix-map.component.scss'],
})
export class AtlasFixMapComponent extends AtlasBaseMapComponent implements OnInit, OnDestroy {
    isPoint: boolean = false;

    public readonly LABEL_ATLASFIX_POINTS = POINTS_LABEL_ATLAS_FIX;
    get pointDetail(): PointDetails {
        return this.atlasFixFilterService.atlasFixService.pointDetails;
    }
    private click$ = new Subject<MapBrowserEvent<MouseEvent>>();
    constructor(
        public atlasFixFilterService: AtlasFixFilterService,
        protected http: HttpClient,
        protected breakpointObserver: BootstrapMediaService,
        protected translationHelper: TranslationHelperService,
        public olMapService: OLMapService,
        private geoserverService: GeoserverAPIService
    ) {
        super(http, breakpointObserver, translationHelper, olMapService);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.atlasFixFilterService.atlasFixService.polygonSubject.subscribe((val) => {
                if (!val) {
                    return;
                }
                this.atlasFixFilterService.setQueryParams();
                this.atlasFixFilterService.atlasFixService.setDefaultFilter();
                this.loadLayer();
                this.loadPoints();
            })
        );
        this.atlasFixFilterService.atlasFixService.getLegends('fix-legend');
        this.atlasFixFilterService.atlasFixService.getFilter('fix-filter');
        this.atlasFixFilterService.atlasFixService.getDetails('fix-detail');

        this.subscriptions.push(
            this.click$
                .pipe(
                    switchMap((event) => {
                        const ep = this.atlasFixFilterService.atlasFixService.createJoinEndpoints(event);
                        console.log('ep',ep);
                        
                        if (!ep) return of(null);

                        return ep.featureCollection.pipe(
                            map((fc) => ({ layer: ep.layer, fc })),
                            catchError(() => of({ layer: ep.layer, fc: null }))
                        );
                    })
                )
                .subscribe((result) => {
                    if (!result || !result.fc || result.fc.features.length == 0) return;
                    this.atlasFixFilterService.atlasFixService.removeVectorLayers();
                    const features = new GeoJSON().readFeatures(result.fc);

                    if (result.layer == environment.geoServer.statsec_details) {
                        if (features.length > 0) {
                            this.addVectorLayer(features[0], new GeoJSON());
                        }
                        this.updatePointDetails(features);
                    } else {
                        const reduced = this.reduceToOnePerLevel(features);
                        this.updateStatSecDetails(features);
                        const statsec = reduced.statsec;
                        const municipality = reduced.municipality;
                        const province = reduced.province;
                        const level = this.atlasFixFilterService.atlasFixService.getLayerName();
                        if (level === 'statsec') {
                            this.addVectorLayer(statsec, new GeoJSON());
                            this.addVectorLayer(municipality, new GeoJSON(), true);
                            if (province.get('name') === 'Région de Bruxelles-Capitale') {
                                this.olMapService.details.push({
                                    label: 'Commune',
                                    value: municipality.get('alternative_name'),
                                });
                            } else {
                                this.olMapService.details.push({
                                    label: 'MUNICIPALITY',
                                    value: municipality.get('name'),
                                });
                            }
                            this.olMapService.details.push({
                                label: 'name',
                                value: statsec.get('name'),
                            });
                            this.olMapService.details.push({
                                label: 'cover',
                                value: `${((statsec.get('filtervalue') as number) * 100).toFixed(2)}%`,
                            });
                        } else {
                            this.olMapService.details.push({
                                label: 'name',
                                value: reduced[level].get('name'),
                            });
                            this.olMapService.details.push({
                                label: 'cover',
                                value: `${((reduced[level].get('filtervalue') as number) * 100).toFixed(2)}%`,
                            });
                            this.addVectorLayer(reduced[level], new GeoJSON());
                        }
                    }
                })
        );

        this.mapOperations();
    }
    mapOperations(): void {
        this.atlasFixFilterService.atlasFixService.eventsKey = this.olMapService.map.on('singleclick', (event: MapBrowserEvent<MouseEvent>) => {
            this.click$.next(event);
        });
    }

    getLayerDetails(feature: Feature<Geometry>): void {
        this.atlasFixFilterService.atlasFixService.getLayerDetails(feature);
    }

    getLayerName(): string {
        return this.atlasFixFilterService.atlasFixService.getLayerName();
    }
    getStyleName(): string {
        return this.atlasFixFilterService.atlasFixService.getStyleName();
    }
    loadLayer(): void {
        this.atlasFixFilterService.atlasFixService.loadLayer();
    }
    loadPoints(): void {
        this.atlasFixFilterService.atlasFixService.loadPoint(null);
    }
    setFilter(): void {
        this.atlasFixFilterService.atlasFixService.setFilter();
    }
    updateDetails(feature: Feature<Geometry>): void {
        this.atlasFixFilterService.atlasFixService.updateDetails(feature);
    }

    updateStatSecDetails(features: Feature<Geometry>[]): void {
        this.atlasFixFilterService.atlasFixService.updateStatSecDetails(features);
    }

    updatePointDetails(features: Feature<Geometry>[]): void {
        this.atlasFixFilterService.atlasFixService.updatePointDetails(features);
    }

    addVectorLayer(feature: Feature<Geometry>, format: GeoJSON, blackBoundries?: boolean) {
        this.atlasFixFilterService.atlasFixService.addVectorLayer(feature, format, blackBoundries);
    }
    ngOnDestroy(): void {
        this.atlasFixFilterService.atlasFixService.mapOperationsUn();
        unsubscribe(this.subscriptions);
    }
    reduceToOnePerLevel(features: Feature[]): {
        statsec?: Feature;
        municipality?: Feature;
        province?: Feature;
        district?: Feature;
        region?: Feature;
        country?: Feature;
    } {
        const byLevel: Record<string, Feature[]> = {
            statsec: [],
            municipality: [],
            province: [],
            district: [],
            region: [],
            country: [],
        };

        features.forEach((f) => {
            const level = f.get('level');
            if (level && byLevel[level]) {
                byLevel[level].push(f);
            }
        });

        const reduce = (level: string): Feature | undefined => {
            const group = byLevel[level];
            if (!group.length) return undefined;

            const seen = new Set<string>();
            const unique = group.filter((f) => {
                const geomStr = new GeoJSON().writeGeometry(f.getGeometry());
                if (seen.has(geomStr)) return false;
                seen.add(geomStr);
                return true;
            });

            return unique[0];
        };

        return {
            statsec: reduce('statsec'),
            municipality: reduce('municipality'),
            province: reduce('province'),
            district: reduce('district'),
            region: reduce('region'),
            country: reduce('country'),
        };
    }
}
